import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhyCareerLiftExistsSection from '../components/WhyCareerLiftExistsSection';
import { AlertCircle, Target, TrendingDown, Clock, BookX, Users } from 'lucide-react';

const WhyStudentsFailPage = () => {
  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <Header />
      
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-amber-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block mb-6">
            <span className="bg-amber-100 text-amber-700 px-6 py-2 rounded-full text-sm font-semibold">THE HARD TRUTH</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
            Why Most Students Fail
            <br className="hidden md:block" />(And How to Avoid It)
          </h1>
          <p className="text-xl text-slate-600 max-w-4xl mx-auto leading-relaxed">It's not about talent. It's about strategy, guidance, and taking action.</p>
        </div>
      </section>

      {/* Reality Stats */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-amber-500 rounded-3xl p-12 text-slate-900 mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center text-white">The Harsh Reality</h2>
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-6xl font-bold text-white mb-2">70%</div>
                <p className="text-xl text-white/90">Give up within 3 months</p>
              </div>
              <div>
                <div className="text-6xl font-bold text-white mb-2">85%</div>
                <p className="text-xl text-white/90">Never complete their goals</p>
              </div>
              <div>
                <div className="text-6xl font-bold text-white mb-2">92%</div>
                <p className="text-xl text-white/90">Lack a clear roadmap</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Reasons */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-slate-900 mb-16">The Top 6 Reasons Students Fail</h2>
          <div className="space-y-12">
            <div className="bg-gradient-to-br from-amber-50 to-white rounded-3xl p-8 md:p-12 border border-amber-100">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-amber-600 rounded-2xl flex items-center justify-center flex-shrink-0"><AlertCircle className="w-8 h-8 text-white" /></div>
                <div>
                  <h3 className="text-3xl font-bold text-slate-900 mb-4">1. Lack of Clear Direction</h3>
                  <p className="text-xl text-slate-700 leading-relaxed mb-4">Most students jump between courses, tutorials, and certifications without a clear roadmap. They learn random skills but never build cohesive expertise that employers value.</p>
                  <div className="bg-amber-100 border-l-4 border-amber-500 p-4 rounded"><p className="text-slate-700 font-semibold mb-2">❌ What Happens:</p><p className="text-slate-600">6 months of scattered learning → No job offers → Frustration and giving up</p></div>
                  <div className="bg-green-100 border-l-4 border-green-500 p-4 rounded mt-4"><p className="text-slate-700 font-semibold mb-2">✅ CareerLift Solution:</p><p className="text-slate-600">Our 4-step Success Framework provides a clear, tested path from beginner to employed professional. No guesswork.</p></div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-white rounded-3xl p-8 md:p-12 border border-amber-100">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-amber-600 rounded-2xl flex items-center justify-center flex-shrink-0"><Clock className="w-8 h-8 text-white" /></div>
                <div>
                  <h3 className="text-3xl font-bold text-slate-900 mb-4">2. Procrastination & Waiting</h3>
                  <p className="text-xl text-slate-700 leading-relaxed mb-4">They wait for the "perfect time" to start. They overthink. They research endlessly but never take action. Meanwhile, the market moves ahead without them.</p>
                  <div className="bg-amber-100 border-l-4 border-amber-500 p-4 rounded"><p className="text-slate-700 font-semibold mb-2">❌ What Happens:</p><p className="text-slate-600">"I'll start next month" → 6 months pass → Still haven't started → Career stagnation</p></div>
                  <div className="bg-green-100 border-l-4 border-green-500 p-4 rounded mt-4"><p className="text-slate-700 font-semibold mb-2">✅ CareerLift Solution:</p><p className="text-slate-600">Live sessions, deadlines, and mentor accountability create urgency. We make "later" become "now."</p></div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-white rounded-3xl p-8 md:p-12 border border-amber-100">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-amber-600 rounded-2xl flex items-center justify-center flex-shrink-0"><BookX className="w-8 h-8 text-white" /></div>
                <div>
                  <h3 className="text-3xl font-bold text-slate-900 mb-4">3. No Practical Experience</h3>
                  <p className="text-xl text-slate-700 leading-relaxed mb-4">Theory is great, but employers want to see what you can BUILD. Certificates mean nothing without projects to back them up.</p>
                  <div className="bg-amber-100 border-l-4 border-amber-500 p-4 rounded"><p className="text-slate-700 font-semibold mb-2">❌ What Happens:</p><p className="text-slate-600">Complete courses → No portfolio → Rejected in interviews → "Need more experience"</p></div>
                  <div className="bg-green-100 border-l-4 border-green-500 p-4 rounded mt-4"><p className="text-slate-700 font-semibold mb-2">✅ CareerLift Solution:</p><p className="text-slate-600">Every course includes hands-on projects: AI chatbots, cloud deployments, DevOps pipelines—portfolio-ready work.</p></div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-white rounded-3xl p-8 md:p-12 border border-amber-100">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center flex-shrink-0"><Users className="w-8 h-8 text-white" /></div>
                <div>
                  <h3 className="text-3xl font-bold text-slate-900 mb-4">4. No Mentor or Guidance</h3>
                  <p className="text-xl text-slate-700 leading-relaxed mb-4">Learning alone is hard. When you get stuck, you stay stuck. No one to ask, no one to push you, no one to guide your decisions.</p>
                  <div className="bg-indigo-50 border-l-4 border-indigo-500 p-4 rounded"><p className="text-slate-700 font-semibold mb-2">❌ What Happens:</p><p className="text-slate-600">Hit a roadblock → Spend days Googling → Get frustrated → Give up on the course</p></div>
                  <div className="bg-green-100 border-l-4 border-green-500 p-4 rounded mt-4"><p className="text-slate-700 font-semibold mb-2">✅ CareerLift Solution:</p><p className="text-slate-600">Live mentor sessions, code reviews, 24/7 AI mentor, and peer community. Never stuck, never alone.</p></div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-white rounded-3xl p-8 md:p-12 border border-amber-100">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center flex-shrink-0"><TrendingDown className="w-8 h-8 text-white" /></div>
                <div>
                  <h3 className="text-3xl font-bold text-slate-900 mb-4">5. Learning Outdated Skills</h3>
                  <p className="text-xl text-slate-700 leading-relaxed mb-4">Many courses teach technologies that are no longer in demand. You spend months learning skills that won't get you hired.</p>
                  <div className="bg-indigo-50 border-l-4 border-indigo-500 p-4 rounded"><p className="text-slate-700 font-semibold mb-2">❌ What Happens:</p><p className="text-slate-600">Complete outdated course → Apply for jobs → "We need modern tech stack" → Start over</p></div>
                  <div className="bg-green-100 border-l-4 border-green-500 p-4 rounded mt-4"><p className="text-slate-700 font-semibold mb-2">✅ CareerLift Solution:</p><p className="text-slate-600">We focus ONLY on skills Australian employers are hiring for RIGHT NOW: AI, Cloud, DevOps, modern frameworks.</p></div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-white rounded-3xl p-8 md:p-12 border border-amber-100">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-green-600 rounded-2xl flex items-center justify-center flex-shrink-0"><Target className="w-8 h-8 text-white" /></div>
                <div>
                  <h3 className="text-3xl font-bold text-slate-900 mb-4">6. No Interview Preparation</h3>
                  <p className="text-xl text-slate-700 leading-relaxed mb-4">You have the skills, but you don't know how to present them. Poor resumes, weak LinkedIn profiles, and nervous interviews kill opportunities.</p>
                  <div className="bg-green-100 border-l-4 border-green-500 p-4 rounded"><p className="text-slate-700 font-semibold mb-2">❌ What Happens:</p><p className="text-slate-600">Apply to 50 jobs → Get 2 interviews → Fail both → Back to square one</p></div>
                  <div className="bg-green-100 border-l-4 border-green-500 p-4 rounded mt-4"><p className="text-slate-700 font-semibold mb-2">✅ CareerLift Solution:</p><p className="text-slate-600">Resume optimization, LinkedIn makeover, mock interviews, and soft skills training. You'll interview like a pro.</p></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <WhyCareerLiftExistsSection />
      
      {/* Final CTA */}
      <section className="py-20 bg-indigo-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Don't Be Another Statistic</h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">The difference between success and failure isn't talent—it's having the right system, guidance, and taking action TODAY.</p>
          <button onClick={() => window.location.href = '/contact'} className="bg-white text-indigo-700 font-semibold px-12 py-4 rounded-md hover:bg-gray-50 transition-colors duration-300">Start Your Success Journey Now</button>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default WhyStudentsFailPage;
